<template lang="pug">
FForm.FContactForm(
  ref="formRef"
  v-slot="{ submit }"
  :initial-values="formInitialValues"
  @valid="handleValidForm"
)
  FGrid(
    columns="6"
    :gap="16"
  )
    FGridItem(v-bind="fullWidthItem")
      FInput(
        name="email"
        :placeholder="t('contact_form__label__email')"
        :error-message="t('forms__error_feedback__email')"
        :rules="[required, email]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="halfWidthItem")
      FInput(
        name="firstname"
        :placeholder="t('contact_form__label__first_name')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="halfWidthItem")
      FInput(
        name="lastname"
        :placeholder="t('contact_form__label__last_name')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FInput(
        name="company"
        :placeholder="t('contact_form__label__organization')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FInput(
        name="jobtitle"
        :placeholder="t('contact_form__label__role')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FPhoneInput(
        name="phone"
        :placeholder="t('contact_form__label__phone')"
        :error-message="t('forms__error_feedback__invalid_phone')"
        :rules="[required]"
        :country-code="currentCountryCode"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FSelect(
        name="contact_motive"
        :placeholder="t('contact_form__label__contact_motive')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        width="100%"
        :options="contactMotiveOptions"
        options-menu-color="neutral--light-5"
        option-text-color="success--dark-1"
        selected-option-color="neutral--light-3"
        selected-option-text-color="success--dark-3"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FTextarea(
        name="message"
        width="100%"
        :placeholder="t('contact_form__label__message')"
        :error-message="t('forms__error_feedback__required')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FCheckbox(
        name="marketingConsent"
        :label="t('forms__marketing_consent')"
        v-bind="contactFormTheme"
      )
    FGridItem(v-bind="fullWidthItem")
      FCheckbox(
        name="privacyPolicyAndTermsConsent"
        :error-message="t('forms__privacy_policy_and_terms_error')"
        :rules="[required]"
        v-bind="contactFormTheme"
      )
        template(#label)
          .FContactForm__legalText__label(
            v-html="t('forms__store_privacy_policy_and_terms_consent')"
          )

    FGridItem(v-bind="fullWidthItem")
      FButton(
        size="giant"
        color="secondary--dark-2"
        background="neutral--light-5"
        focus-color="success--light-1"
        anchor-point="left"
        :loading="loading"
        @click="submit"
      ) {{ t('contact_form__submit_button') }}

    FGridItem.Form__messages(v-bind="fullWidthItem")
      .Form__message--success(
        v-if="success"
        v-html="t('contact_form__success_message')"
      )
      .Form__message--error(
        v-else-if="error"
        v-html="t('contact_form__error_message')"
      )
</template>

<script setup lang="ts">
import { email, required } from '@vee-validate/rules';

import { localesToCountryCode } from '@/config/locales';

import type { FForm, FGridItemProps } from '@fifteen/design-system-vue';

const { t } = useI18n();
const { locale } = useI18n();
const currentCountryCode = computed(() => localesToCountryCode[locale.value]);

const formInitialValues: Record<string, unknown> = {
  email: '',
  firstname: '',
  lastname: '',
  jobtitle: '',
  company: '',
  contact_motive: '',
  message: '',
  phone: '',
  storeAndProcessConsent: false,
  marketingConsent: false,
};

const contactMotiveOptions = computed(() => [
  {
    label: t('contact_form__contact_motive__evaluate_for_my_city'),
    value: 'Evaluate Fifteen for my city',
  },
  {
    label: t('contact_form__contact_motive__interested_in_technology'),
    value: 'Interested in Fifteen’s technology for micromobility operators',
  },
  {
    label: t('contact_form__contact_motive__electric_bikes_for_employees'),
    value: 'Considering electric bikes for my employees',
  },
  {
    label: t('contact_form__contact_motive__product_question'),
    value: 'Product question',
  },
  {
    label: t('contact_form__contact_motive__something_else'),
    value: 'Something else',
  },
]);

const config = useRuntimeConfig();
const { success, error, loading, submitForm } = useHubspotForm();

async function handleValidForm(
  values: typeof formInitialValues
): Promise<void> {
  await submitForm(config.public.hubspotContactFormId, values, {
    storeAndProcess: {
      field: 'privacyPolicyAndTermsConsent',
      text: t('forms__store_process_consent'),
    },
    marketing: [
      {
        field: 'marketingConsent',
        text: t('forms__marketing_consent'),
        id: config.public.hubspotMarketingSubscriptionId,
      },
    ],
  });
  resetForm();
}

const formRef = ref<InstanceType<typeof FForm>>();

async function resetForm(): Promise<void> {
  formRef.value?.reset();
}

const contactFormTheme = {
  errorColor: 'primary',
  placeholderTextColor: 'success--dark-1',
  textColor: 'success--dark-2',
  color: 'success--light-2',
  borderColor: 'success--dark-2',
  focusBorderColor: 'success--dark-2',
  focusColor: 'neutral--light-5',
};

const fullWidthItem: FGridItemProps = {
  span: 6,
  lg: 6,
  md: 6,
  sm: 6,
  xs: 6,
  xxs: 6,
};
const halfWidthItem: FGridItemProps = {
  span: 3,
  lg: 3,
  md: 3,
  sm: 6,
  xs: 6,
  xxs: 6,
};
</script>
